<template>
  <div>
    <template 
      v-for="(block, blockIndex) in blocks" 
      :key="block.type"
    >
      <template v-if="block.type === 'combined_metafield'">
        <template v-if="block.settings.metafield === 'text.short_description'">
          <div
            class="product__text product__short-description rte"
            :data-block-index="blockIndex"
            v-html="combinedShortDescription"
          /> 
        </template>

        <template v-if="block.settings.metafield === 'descriptors.subtitle'">
          <p class="product__subtitle">
            {{ combinedSubtitle }}
          </p>
        </template>

        <template v-if="block.settings.metafield === 'custom.offer_information'">
          <div
            class="offer-information"
            v-html="combinedOfferInformation"
          />
        </template>
      </template>

      <template v-if="block.type === 'breadcrumb'">
        <div class="product__breadcrumb">
          <slot name="breadcrumb" />
        </div>
      </template>

      <template v-if="block.type === 'title'">
        <div
          class="product__title-container"
          :class="{ 'hide-reviews-summary': !block.settings.show_reviews_summary }"
        >
          <div>
            <template v-if="block.settings.show_review_stars">
              <JudgeMeStars
                v-if="isCombinedListingsProduct"
                :product="combinedListingsProduct"
              />
              <JudgeMeStars
                v-else
                :product="product"
              />
            </template>

            <h1 class="product__title h4">
              {{ product.title }}
            </h1>

            <template v-if="block.settings.subtitle">
              <p class="product__subtitle">
                {{ block.settings.subtitle }}
              </p>
            </template>
          </div>

          <template v-if="block.settings.show_price">
            <div class="product__price-line-wrapper">
              <div class="product__price-line flex items-center">
                <template v-if="!product.tags.includes('gift-card')">
                  <p class="product__price">
                    {{ product.price_varies ? `From ${priceFormatted}` : priceFormatted }}
                    <s v-if="compareAtPrice">
                      {{ compareAtPrice }}
                    </s>
                  </p>
                </template>
              </div>

              <template v-if="product.metafields.numberOfServings">
                <p class="product__serving-price">
                  /{{ pricePerServing }} per serving
                </p>
              </template>
            </div>
          </template>
        </div>
      </template>

      <template v-if="block.type === 'split_payment'">
        <div
          v-if="instalments.enabled"
          class="product__instalments flex items-center"
        >
          <KlarnaIcon v-if="instalments.provider === 'klarna'" />
          <AfterpayIcon v-if="instalments.provider === 'afterpay'" />
          <ClearpayIcon v-if="instalments.provider === 'clearpay'" />
          <p v-html="instalmentsText" />
        </div>
      </template>

      <template v-if="block.type === 'badges'">
        <div
          v-if="badges"
          class="product__badges flex flex-wrap"
        >
          <div
            v-for="(badge, index) in badges"
            :key="`${product.handle}-badge-${index}`"
            class="product-badge"
          >
            {{ badge }}
          </div>
        </div>
      </template>

      <template v-if="block.type === 'text'">
        <div
          class="product__text product__short-description rte"
          :data-block-index="blockIndex"
          v-html="block.settings.text"
        />
      </template>

      <template v-if="block.type === 'embed'">
        <slot name="embed" />
      </template>

      <template v-if="block.type === 'payment_options'">
        <slot name="payment-options" />
      </template>

      <template v-if="block.type === 'usps'">
        <slot name="usps" />
      </template>

      <template v-if="block.type === 'elite_points'">
        <slot name="elite-points" />
      </template>

      <template v-if="block.type === 'offer_information'">
        <slot name="offer-information" />
      </template>

      <template v-if="block.type === 'shipping'">
        <slot name="shipping" />
      </template>
    </template>

    <div
      v-if="shortDescription"
      class="product__short-description rte"
    >
      <p>{{ shortDescription }}</p>
    </div>

    <div
      v-if="flavourCollectionSize > 1 && blocks.find(block => block.type === 'product_form')?.settings?.enable_serving_selector !== false"
      class="product__flavour-collection"
    >
      <h2 class="product__label">
        {{ titles.chooseServing }}
      </h2>

      <ul class="product__flavour-colleciton-list text-center flex">
        <li
          v-for="flavourCollectionProduct in flavourCollection.products"
          :key="flavourCollectionProduct.handle"
          class="product__flavour-collection-list-item"
          :class="{ 'product__flavour-collection-list-item--active': flavourCollectionProduct.handle === product.handle }"
        >
          <a :href="`/products/${flavourCollectionProduct.handle}`">
            <div class="product__flavour-collection-list-item-image">
              <img
                :src="flavourCollectionProduct.image"
                :alt="`${ flavourCollectionProduct.title }-image`"
                width="34"
              >
            </div>
            <h3 class="product__flavour-collection-list-item-title xs">
              {{ flavourCollectionProduct.title }}
            </h3>
          </a>
        </li>
      </ul>
    </div>

    <div
      v-if="!suppressVariantCollectionUi"
      class="product__variant-collection"
    >
      <h2 class="product__label">
        {{ variantCollectionTitle }}
      </h2>

      <button
        class="variant-collection-dropdown-proxy sm flex justify-between items-center bg-white border-2 border-black hover:border-red"
        @click="openSlideout"
      >
        <img
          :key="`${product.handle}-flavour-dropdown-image`"
          :src="variantCollectionDropdownImage"
          :alt="`${product.handle}-flavour-dropdown-image`"
          width="24"
        >
        {{ variantCollectionDropdownVariantTitle.split(' / ')[0] }}
        <ChevronRightIcon />
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useProductStore } from '../../pinia/ProductStore'
import { useVariantCollectionStore } from '../../pinia/VariantCollectionStore'

import { productPrice } from '../../helpers/product'

// import HeartIcon from '../../icons/heart-icon'
import KlarnaIcon from '../../icons/klarna-icon'
import AfterpayIcon from '../../icons/afterpay-icon'
import ClearpayIcon from '../../icons/clearpay-icon'
import ChevronRightIcon from '../../icons/chevron-right'

import JudgeMeStars from '../../components/product-judgeme-stars'

export default {
  components: {
    // HeartIcon,
    KlarnaIcon,
    AfterpayIcon,
    ClearpayIcon,
    ChevronRightIcon,
    JudgeMeStars
  },
  props: {
    instalments: {
      type: Object,
      default: () => {
        return {
          enabled: false
        }
      }
    },
    titles: {
      type: Object,
      default: null
    },
    variantCollectionIsFlavourDropdown: {
      type: Boolean,
      default: false
    },
    suppressVariantCollectionUi: {
      type: Boolean,
      default: false
    },
    isCombinedListingsProduct: {
      type: Boolean,
      default: false
    },
    productType: {
      type: String,
      default: ''
    },
    blocks: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      products: [],
      flavourCollections: [...window.theme.flavourCollections]
    }
  },
  computed: {
    ...mapState (useProductStore, [
      'product',
      'priceFormatted',
      'badges',
      'shortDescription',
      'flavourCollectionHandle',
      'variantCollectionTriggerImage',
      'variantTriggerImage',
      'selectedVariant',
      'combinedListingsProduct'
    ]),

    variantCollectionDropdownImage () {
      if (this.isCombinedListingsProduct) {
        return this.variantTriggerImage
      } else {
        return this.variantCollectionTriggerImage
      }
    },

    variantCollectionDropdownVariantTitle () {
      if (this.isCombinedListingsProduct) {
        return this.selectedVariant.title.toLowerCase()
      } else {
        return this.product.title.toLowerCase()
      }
    },

    flavourCollection () {
      if (this.flavourCollectionHandle) {
        return this.flavourCollections?.find(collection => collection.handle === this.flavourCollectionHandle)
      }
      return null
    },

    flavourCollectionSize () {
      if (this.flavourCollectionHandle) {
        return this.flavourCollection?.products?.length
      }
      return 0
    },

    variantCollectionTitle () {
      if (this.variantCollectionIsFlavourDropdown) {
        return this.titles.chooseFlavour
      } else if (this.productType === 'shaker') {
        return this.titles.chooseShaker
      } else if (this.isCombinedListingsProduct) {
        return this.titles.chooseVariant
      } else {
        return 'Select:'
      }
    },

    instalmentsText () {
      if (this.instalments.enabled) {
        const instalmentsPrice = productPrice(this.product.price / this.instalments.amount)
        return this.instalments.text.replace('[AMOUNT]', this.instalments.amount)
          .replace('[PRICE]', instalmentsPrice)
      }
      return null
    },

    compareAtPrice () {
      return this.product.compare_at_price && this.product.compare_at_price > this.product.price ? productPrice(this.product.compare_at_price) : null
    },

    pricePerServing () {
      if (this.product.metafields.numberOfServings) {
        const servings = this.product.metafields.numberOfServings
        const pricePerServing = this.product.price / servings
        return productPrice(pricePerServing)
      }
      return null
    },

    combinedShortDescription () {
      return this.combinedListingsProduct ? this.combinedListingsProduct.metafields.shortDescription : null
    },

    combinedSubtitle () {
      return this.combinedListingsProduct ? this.combinedListingsProduct.metafields.subtitle : null
    },

    combinedOfferInformation () {
      return this.combinedListingsProduct ? this.combinedListingsProduct.metafields.offerInformation : null
    }
  },
  methods: {
    ...mapActions(useVariantCollectionStore, [
      'openSlideout'
    ]),
  }
}
</script>
